import { Typography } from '@ag1/ui';
import { CartLine } from '@portal/api/shopifyV2/types';
import { BundleSavings } from '@portal/shared/components/BundleSavings/BundleSavings';
import { Fragment, useMemo } from 'react';
import { NO_CADENCE_KEY } from '../../util';
import { SiteCartItem } from '../SiteCartItem/SiteCartItem';
import styles from './SiteCartSection.module.scss';

interface Cadence {
  id: string;
  name: string;
  items: CartLine[];
}

type SiteCartSectionProps = {
  sectionName: string;
  items: CartLine[];
  isSubscription?: boolean;
};

export const SiteCartSection = ({
  sectionName = '',
  items = [],
  isSubscription = false
}: SiteCartSectionProps) => {
  const itemsByCadence = useMemo(() => {
    const noCadenceGroup: Cadence = {
      id: NO_CADENCE_KEY,
      name: '',
      items: [] as CartLine[]
    };

    const groupedItems = items.reduce(
      (acc, item) => {
        const cadence = item.sellingPlanAllocation?.sellingPlan;
        if (!cadence) return acc;

        // Ignore bundle items
        if (item.attributes?.find((attr) => attr.key === '__isBundleItem')?.value === 'true') {
          noCadenceGroup.items.push(item);
          return acc;
        }

        const { id, name } = cadence;

        if (!acc[id]) {
          acc[id] = { id, name, items: [] };
        }
        acc[id].items.push(item);

        return acc;
      },
      {} as Record<string, Cadence>
    );
    const sortedCadenceGroups = Object.values(groupedItems).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    // Keep no cadence items at the top
    return [noCadenceGroup, ...sortedCadenceGroups];
  }, [items]);

  if (!items?.length) return null;

  return (
    <div className={styles.container}>
      <div className={styles.titleContainer}>
        <Typography variant="captionMono" size="1" className={styles.sectionTitle}>
          {sectionName}
        </Typography>
      </div>
      {/* OTP / Free */}
      {!isSubscription &&
        items.map((item) => (
          <SiteCartItem key={item.id} item={item} isSubscription={isSubscription} />
        ))}
      {/* Sub By Cadence */}
      {isSubscription &&
        itemsByCadence.map((cadence) => (
          <Fragment key={cadence.id}>
            {cadence.id !== NO_CADENCE_KEY && (
              <div className={styles.cadenceSelector}>
                <Typography variant="portalCaption" size="1" noPadding color="brand-green-new">
                  {cadence.name}
                </Typography>
              </div>
            )}
            {cadence.items.map((item) => (
              <>
                <SiteCartItem
                  key={`${item.id}-${item.merchandise.id}-${item.sellingPlanAllocation?.sellingPlan.id}`}
                  item={item}
                  isSubscription={isSubscription}
                />
                {/* Bundle Savings Message */}
                <BundleSavings
                  key={item.id}
                  itemId={item.merchandise.id}
                  itemQuantity={item.quantity}
                  currencyCode={item?.cost?.totalAmount?.currencyCode || 'USD'}
                  isSubscription={isSubscription}
                />
              </>
            ))}
          </Fragment>
        ))}
    </div>
  );
};
